import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import EntityManagementPage, { EntityField } from './EntityManagementPage';
import { EventVenue } from '../../types';
import { contextError } from '../../utils/operatingContext';
import { useAppSelector } from '../../store/hooks';
import { getOperatingContext, isOperatingContextReady, useOperatingContext } from '../../utils/operatingContextLifecycle';

const fields: EntityField[] = [
  { key: 'name', label: 'Назва місця', required: true },
  { key: 'address', label: 'Адреса' },
  { key: 'description', label: 'Опис', type: 'textarea' },
];
type CreationAccess = {
  operatingGroupId: number | null;
  systemAdministrator: boolean;
  allGroupsOverview: boolean;
  canEdit: boolean;
};

const EventVenuesPage: React.FC = () => {
  const context = useOperatingContext();
  const token = useAppSelector(state => state.auth.token);
  if (!isOperatingContextReady(context, token))
    return <p role="alert">{context.error || 'Оберіть підтверджену операційну групу для роботи з майданчиками.'}</p>;
  return <EventVenuesWorkspace key={`${context.generation}:${context.groupId}`}
    contextGeneration={context.generation} groupId={context.groupId} token={token} />;
};

const EventVenuesWorkspace: React.FC<{
  contextGeneration: number; groupId: string | null; token: string | null;
}> = ({ contextGeneration, groupId, token }) => {
  const generation = useRef(0);
  const writing = useRef(false);
  const [access, setAccess] = useState<CreationAccess | null>(null);
  const [items, setItems] = useState<EventVenue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invalidateRequests = useCallback(() => ++generation.current, []);
  const currentContext = useCallback(() => {
    const context = getOperatingContext();
    return context.generation === contextGeneration && context.groupId === groupId &&
      isOperatingContextReady(context, token);
  }, [contextGeneration, groupId, token]);
  useEffect(() => () => { invalidateRequests(); }, [invalidateRequests]);

  const load = useCallback(() => async () => {
    if (!currentContext()) return;
    const current = generation.current;
    setBusy(true);
    try {
      const [scope, venues] = await Promise.all([
        api.get<CreationAccess>('/eventvenues/creation-access'),
        api.get<EventVenue[]>('/eventvenues'),
      ]);
      if (current !== generation.current || !currentContext()) return;
      if (String(scope.data.operatingGroupId ?? 'all') !== groupId ||
          scope.data.allGroupsOverview !== (groupId === 'all'))
        throw new Error('Контекст майданчиків не відповідає підтвердженій групі. Оновіть сторінку.');
      setAccess(scope.data);
      setItems(venues.data);
    } catch (failure: unknown) {
      if (current !== generation.current || !currentContext()) return;
      setAccess(null);
      setItems([]);
      setError(contextError(failure));
    } finally {
      if (current === generation.current && currentContext()) setBusy(false);
    }
  }, [currentContext, groupId]);

  const mutate = async (operation: () => Promise<unknown>) => {
    if (!currentContext() || groupId === 'all' || writing.current) return;
    writing.current = true;
    const current = generation.current;
    setBusy(true);
    setError(null);
    try {
      await operation();
      if (current !== generation.current || !currentContext()) return;
      const response = await api.get<EventVenue[]>('/eventvenues');
      if (current === generation.current && currentContext()) setItems(response.data);
    } catch (failure: unknown) {
      if (current !== generation.current || !currentContext()) return;
      // Fail closed after a rejected write, including a cross-tab selection conflict.
      invalidateRequests();
      setAccess(null);
      setItems([]);
      setBusy(false);
      setError(contextError(failure));
    } finally {
      writing.current = false;
      if (current === generation.current && currentContext()) setBusy(false);
    }
  };
  const canEdit = !!access && access.canEdit && !access.allGroupsOverview;
  const canCreate = canEdit && access?.systemAdministrator === true;
  return (
    <>
      <Link to="/management/venue-layouts">Шаблони та плани залів</Link>
      <p role="note">
        У робочій групі створювати майданчики може лише системний адміністратор.
        Власність групи не надає доступ автоматично: працівникам його призначають окремо для кожного ресурсу.
      </p>
      {error && <p role="alert">{error}</p>}
      <EntityManagementPage<EventVenue>
        title="Управління місцями проведення"
        items={items}
        loading={busy}
        submitting={busy}
        error={null}
        fields={fields}
        fetchAll={load}
        canCreate={canCreate}
        canEdit={canEdit}
        createItem={data => async () => { if (canCreate) await mutate(() => api.post('/eventvenues', data)); }}
        updateItem={({ id, data }) => async () => { if (canEdit) await mutate(() => api.put(`/eventvenues/${id}`, data)); }}
        deleteItem={id => async () => { if (canEdit) await mutate(() => api.delete(`/eventvenues/${id}`)); }}
      />
    </>
  );
};

export default EventVenuesPage;
